// The in-app guide (SPEC §19, 2026-10-01): how to use the dialer and the lead engine, for every signed-in user.
// Content lives in web/src/content/guide.ts (GUIDE_SECTIONS); a body line starting with '- ' is a bullet.
import type { ComponentChildren } from 'preact';
import { GUIDE_SECTIONS, type GuideSection } from '../content/guide';
import { useMe } from '../me';
import '../admin/admin.css';

/** Paragraphs and runs of '- ' bullets, in order. Exported for tests. */
export function guideBlocks(body: string[]): Array<{ kind: 'p'; text: string } | { kind: 'ul'; items: string[] }> {
  const out: Array<{ kind: 'p'; text: string } | { kind: 'ul'; items: string[] }> = [];
  for (const line of body) {
    if (line.startsWith('- ')) {
      const last = out[out.length - 1];
      if (last && last.kind === 'ul') last.items.push(line.slice(2));
      else out.push({ kind: 'ul', items: [line.slice(2)] });
    } else if (line.trim()) {
      out.push({ kind: 'p', text: line });
    }
  }
  return out;
}

/** Bare https links become clickable; everything else stays plain text. */
function linkify(text: string): ComponentChildren {
  const parts = text.split(/(https?:\/\/[^\s)]+)/g);
  return parts.map((p, i) => (i % 2 === 1 ? <a href={p} target={p.startsWith(location.origin) ? undefined : '_blank'} rel="noopener noreferrer">{p}</a> : p));
}

function Section(props: { s: GuideSection }) {
  return (
    <section id={props.s.id} class="card stack" style="gap:10px;scroll-margin-top:16px">
      <h2 style="margin:0">{props.s.title}</h2>
      {guideBlocks(props.s.body).map((b) => (b.kind === 'p'
        ? <p style="margin:0;max-width:78ch">{linkify(b.text)}</p>
        : <ul style="margin:0;padding-left:20px;max-width:78ch;display:flex;flex-direction:column;gap:4px">{b.items.map((t) => <li>{linkify(t)}</li>)}</ul>))}
    </section>
  );
}

export function Guide(_props: { params: Record<string, string> }) {
  const me = useMe();
  return (
    <div class="adm-page" style="max-width:1100px">
      <div class="adm-head">
        <div>
          <h1>Guide</h1>
          <div class="adm-sub">How the Valistix Dialer and the lead engine work, {me.name.split(' ')[0]}. Questions: <a href="mailto:pj@valistix.com">pj@valistix.com</a>.</div>
        </div>
      </div>
      <div style="display:grid;grid-template-columns:220px 1fr;gap:16px;align-items:start">
        <nav class="card stack" style="gap:4px;position:sticky;top:12px" aria-label="Guide sections">
          <span class="eyebrow">On this page</span>
          {GUIDE_SECTIONS.map((s) => <a href={`#${s.id}`} style="text-decoration:none;font-size:14px">{s.title}</a>)}
        </nav>
        <div class="stack" style="gap:14px">
          {GUIDE_SECTIONS.map((s) => <Section s={s} />)}
        </div>
      </div>
    </div>
  );
}
